import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { careerApiRepository } from '../../infrastructure/careerApiRepository';
import '../styles/landing.css';

const EMPLOYMENT_TYPE_LABEL = {
    FULL_TIME: '정규직',
    CONTRACT: '계약직',
    INTERN: '인턴',
};

function calcDday(deadlineAt) {
    if (!deadlineAt) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineAt);
    deadline.setHours(0, 0, 0, 0);
    const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return diff;
}

function formatDateRange(createdAt, deadlineAt) {
    const fmt = (s) => {
        const d = new Date(s);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };
    if (!deadlineAt) return `${fmt(createdAt)} ~ 상시`;
    return `${fmt(createdAt)} ~ ${fmt(deadlineAt)}`;
}

function DdayBadge({ deadlineAt }) {
    if (!deadlineAt) {
        return <span className="cp-dday cp-dday--always">상시</span>;
    }
    const diff = calcDday(deadlineAt);
    if (diff === 0) return <span className="cp-dday cp-dday--urgent">D-Day</span>;
    if (diff < 0) return <span className="cp-dday cp-dday--closed">마감</span>;
    return <span className={`cp-dday ${diff <= 7 ? 'cp-dday--urgent' : ''}`}>D-{diff}</span>;
}

function ShareButton({ title }) {
    const handleShare = (e) => {
        e.stopPropagation();
        if (navigator.share) {
            navigator.share({ title, url: window.location.href });
        } else {
            navigator.clipboard.writeText(window.location.href);
        }
    };
    return (
        <button className="cp-icon-btn" onClick={handleShare} title="공유">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
        </button>
    );
}

export function CareersPage() {
    const navigate = useNavigate();
    const [postings, setPostings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [deptFilter, setDeptFilter] = useState('전체');
    const [typeFilter, setTypeFilter] = useState('전체');
    const [sort, setSort] = useState('latest');
    const [deptOpen, setDeptOpen] = useState(false);
    const [typeOpen, setTypeOpen] = useState(false);

    const deptRef = useRef(null);
    const typeRef = useRef(null);

    useEffect(() => {
        careerApiRepository.getCareers()
            .then(setPostings)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const handleClick = (e) => {
            if (deptRef.current && !deptRef.current.contains(e.target)) setDeptOpen(false);
            if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const departments = ['전체', ...Array.from(new Set(postings.map(p => p.department).filter(Boolean)))];
    const types = ['전체', ...Array.from(new Set(postings.map(p => p.employmentType).filter(Boolean)))];

    const filtered = postings
        .filter(p => deptFilter === '전체' || p.department === deptFilter)
        .filter(p => typeFilter === '전체' || p.employmentType === typeFilter)
        .filter(p => {
            if (!search.trim()) return true;
            const q = search.trim().toLowerCase();
            return p.title.toLowerCase().includes(q) || (p.department || '').toLowerCase().includes(q);
        })
        .sort((a, b) => {
            if (sort === 'latest') {
                return new Date(b.createdAt) - new Date(a.createdAt);
            }
            // 마감순: null(상시)은 맨 뒤
            if (!a.deadlineAt && !b.deadlineAt) return 0;
            if (!a.deadlineAt) return 1;
            if (!b.deadlineAt) return -1;
            return new Date(a.deadlineAt) - new Date(b.deadlineAt);
        });

    return (
        <>
            <div className="cp-wrap" style={{ minHeight: '100vh', background: '#fff' }}>

                {/* 헤더 */}
                <div className="cp-header">
                    <div className="cp-header-inner">
                        <h1 className="cp-header-title">채용공고</h1>
                        <div className="cp-search-box">
                            <input
                                className="cp-search-input"
                                type="text"
                                placeholder="부서, 직무로 검색해보세요."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            <button className="cp-search-btn">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="cp-divider" />

                {/* 필터 바 */}
                <div className="cp-filter-bar">
                    <div className="cp-filter-bar-inner">
                        <div className="cp-filter-left">
                            {/* 부서 드롭다운 */}
                            <div className="cp-dropdown" ref={deptRef}>
                                <button
                                    className={`cp-filter-btn ${deptFilter !== '전체' ? 'cp-filter-btn--active' : ''}`}
                                    onClick={() => { setDeptOpen(v => !v); setTypeOpen(false); }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                                        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                                        <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                                    </svg>
                                    {deptFilter === '전체' ? '부서 선택' : deptFilter}
                                </button>
                                {deptOpen && (
                                    <div className="cp-dropdown-menu">
                                        {departments.map(d => (
                                            <button
                                                key={d}
                                                className={`cp-dropdown-item ${deptFilter === d ? 'cp-dropdown-item--active' : ''}`}
                                                onClick={() => { setDeptFilter(d); setDeptOpen(false); }}
                                            >
                                                {d}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* 고용형태 드롭다운 */}
                            <div className="cp-dropdown" ref={typeRef}>
                                <button
                                    className={`cp-filter-btn ${typeFilter !== '전체' ? 'cp-filter-btn--active' : ''}`}
                                    onClick={() => { setTypeOpen(v => !v); setDeptOpen(false); }}
                                >
                                    {typeFilter === '전체' ? '고용형태' : EMPLOYMENT_TYPE_LABEL[typeFilter] || typeFilter}
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                </button>
                                {typeOpen && (
                                    <div className="cp-dropdown-menu">
                                        {types.map(t => (
                                            <button
                                                key={t}
                                                className={`cp-dropdown-item ${typeFilter === t ? 'cp-dropdown-item--active' : ''}`}
                                                onClick={() => { setTypeFilter(t); setTypeOpen(false); }}
                                            >
                                                {t === '전체' ? '전체' : (EMPLOYMENT_TYPE_LABEL[t] || t)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 정렬 */}
                        <div className="cp-sort">
                            <button
                                className={`cp-sort-btn ${sort === 'latest' ? 'cp-sort-btn--active' : ''}`}
                                onClick={() => setSort('latest')}
                            >
                                최신순
                            </button>
                            <span className="cp-sort-divider">|</span>
                            <button
                                className={`cp-sort-btn ${sort === 'deadline' ? 'cp-sort-btn--active' : ''}`}
                                onClick={() => setSort('deadline')}
                            >
                                마감순
                            </button>
                        </div>
                    </div>
                </div>

                {/* 목록 */}
                <section className="cp-section">
                    <div className="cp-container">
                        {loading ? (
                            <div className="cp-center">
                                <div className="cp-spinner" />
                                <p>채용 정보를 불러오는 중...</p>
                            </div>
                        ) : error ? (
                            <div className="cp-center">
                                <p className="cp-notice-title">잠시 후 다시 확인해 주세요</p>
                                <p className="cp-notice-sub">채용 정보를 불러오는 중 오류가 발생했습니다.</p>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="cp-center">
                                <p className="cp-notice-title">검색 결과가 없습니다</p>
                                <p className="cp-notice-sub">다른 검색어 또는 필터를 시도해보세요.</p>
                            </div>
                        ) : (
                            <div className="cp-grid">
                                {filtered.map((posting) => (
                                    <div
                                        key={posting.id}
                                        className="cp-card"
                                        onClick={() => navigate(`/careers/${posting.id}`)}
                                    >
                                        <div className="cp-card-actions">
                                            <ShareButton title={posting.title} />
                                        </div>

                                        {/* 부서 + 타이틀: 같은 왼쪽 기준선 */}
                                        <div className="cp-card-body">
                                            {posting.department && (
                                                <p className="cp-card-dept">{posting.department}</p>
                                            )}
                                            <h3 className="cp-card-title">{posting.title}</h3>
                                        </div>

                                        <p className="cp-card-meta">
                                            {EMPLOYMENT_TYPE_LABEL[posting.employmentType] || posting.employmentType}
                                            <span className="cp-meta-sep">|</span>
                                            {formatDateRange(posting.createdAt, posting.deadlineAt)}
                                        </p>

                                        <div className="cp-card-tags">
                                            <DdayBadge deadlineAt={posting.deadlineAt} />
                                            {posting.department && (
                                                <span className="cp-tag">{posting.department}</span>
                                            )}
                                            <span className="cp-tag">
                                                {EMPLOYMENT_TYPE_LABEL[posting.employmentType] || posting.employmentType}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
            <Footer />

            <style>{`
                /* ── 레이아웃 기준선: 좌우 60px 패딩, max-width 1100px ── */
                .cp-wrap { --side: 60px; --maxw: 1100px; }

                /* 헤더 */
                .cp-header {
                    padding: 44px var(--side, 60px) 32px;
                    background: #fff;
                }
                .cp-header-inner {
                    max-width: var(--maxw, 1100px);
                    margin: 0 auto;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 24px;
                }
                .cp-header-title {
                    font-size: 1.875rem;
                    font-weight: 800;
                    color: #1c1814;
                    margin: 0;
                    letter-spacing: -0.5px;
                    flex-shrink: 0;
                }
                .cp-search-box {
                    display: flex;
                    align-items: center;
                    border: 1.5px solid #d0cbc6;
                    border-radius: 6px;
                    overflow: hidden;
                    width: 320px;
                    background: #fff;
                    flex-shrink: 0;
                }
                .cp-search-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    padding: 9px 14px;
                    font-size: 0.875rem;
                    color: #333;
                    background: transparent;
                }
                .cp-search-input::placeholder { color: #bbb; }
                .cp-search-btn {
                    background: #1c1814;
                    border: none;
                    padding: 0 14px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #fff;
                    flex-shrink: 0;
                    transition: background 0.15s;
                }
                .cp-search-btn:hover { background: #f16102; }

                .cp-divider { height: 1px; background: #e5e0db; }

                /* 필터 바 — 헤더와 동일한 좌우 기준선 */
                .cp-filter-bar {
                    padding: 0 var(--side, 60px);
                    background: #fff;
                    border-bottom: 1px solid #e5e0db;
                }
                .cp-filter-bar-inner {
                    max-width: var(--maxw, 1100px);
                    margin: 0 auto;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    height: 56px;
                }
                .cp-filter-left { display: flex; align-items: center; gap: 8px; }

                /* 드롭다운 */
                .cp-dropdown { position: relative; }
                .cp-filter-btn {
                    display: flex;
                    align-items: center;
                    border: 1.5px solid #d0cbc6;
                    border-radius: 6px;
                    background: #fff;
                    padding: 6px 12px;
                    font-size: 0.825rem;
                    font-weight: 500;
                    color: #555;
                    cursor: pointer;
                    transition: all 0.15s;
                    white-space: nowrap;
                    height: 34px;
                }
                .cp-filter-btn:hover,
                .cp-filter-btn--active {
                    border-color: #1c1814;
                    color: #1c1814;
                    font-weight: 600;
                }
                .cp-dropdown-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    background: #fff;
                    border: 1.5px solid #d0cbc6;
                    border-radius: 8px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
                    min-width: 140px;
                    z-index: 100;
                    padding: 4px 0;
                }
                .cp-dropdown-item {
                    display: block;
                    width: 100%;
                    text-align: left;
                    border: none;
                    background: none;
                    padding: 8px 16px;
                    font-size: 0.825rem;
                    color: #444;
                    cursor: pointer;
                    transition: background 0.1s;
                }
                .cp-dropdown-item:hover { background: #f7f4f1; color: #1c1814; }
                .cp-dropdown-item--active { font-weight: 700; color: #f16102; }

                /* 정렬 */
                .cp-sort { display: flex; align-items: center; gap: 0; }
                .cp-sort-btn {
                    border: none;
                    background: none;
                    font-size: 0.825rem;
                    color: #aaa;
                    cursor: pointer;
                    padding: 4px 10px;
                    transition: color 0.15s;
                    letter-spacing: 0.2px;
                }
                .cp-sort-btn:hover { color: #333; }
                .cp-sort-btn--active { color: #1c1814; font-weight: 700; }
                .cp-sort-divider { color: #ddd; font-size: 0.75rem; }

                /* 목록 영역 — 헤더·필터와 동일한 좌우 기준선 */
                .cp-section {
                    padding: 32px var(--side, 60px) 80px;
                    background: #f8f7f5;
                }
                .cp-container { max-width: var(--maxw, 1100px); margin: 0 auto; }

                /* 카드 그리드 */
                .cp-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                }

                /* 카드 */
                .cp-card {
                    background: #fff;
                    border: 1px solid #e8e3de;
                    border-radius: 10px;
                    padding: 20px 20px 18px;
                    cursor: pointer;
                    transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    min-height: 190px;
                }
                .cp-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
                    border-color: #c5bcb4;
                }
                .cp-card-actions {
                    position: absolute;
                    top: 16px;
                    right: 14px;
                    display: flex;
                    gap: 2px;
                }
                .cp-icon-btn {
                    width: 28px;
                    height: 28px;
                    border: none;
                    background: none;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #bbb;
                    transition: all 0.15s;
                }
                .cp-icon-btn:hover { background: #f0ece8; color: #555; }

                /* 부서 + 타이틀 묶음: 같은 왼쪽 기준선 */
                .cp-card-body {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    padding-right: 36px; /* 공유 아이콘 영역 확보 */
                    flex: 1;
                }
                .cp-card-dept {
                    font-size: 0.775rem;
                    color: #aaa;
                    margin: 0;
                    line-height: 1.3;
                }
                .cp-card-title {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #1c1814;
                    margin: 0;
                    line-height: 1.5;
                }
                .cp-card-meta {
                    font-size: 0.775rem;
                    color: #999;
                    margin: 12px 0 0 0;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .cp-meta-sep { color: #ddd; }

                /* 태그 */
                .cp-card-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                    margin-top: 6px;
                }
                .cp-dday {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 42px;
                    height: 24px;
                    padding: 0 8px;
                    border-radius: 12px;
                    font-size: 0.72rem;
                    font-weight: 700;
                    background: #1c1814;
                    color: #fff;
                    letter-spacing: 0.3px;
                }
                .cp-dday--urgent { background: #f16102; }
                .cp-dday--always { background: #3d9e6e; }
                .cp-dday--closed { background: #bbb; }
                .cp-tag {
                    display: inline-flex;
                    align-items: center;
                    height: 24px;
                    padding: 0 9px;
                    border-radius: 12px;
                    font-size: 0.72rem;
                    font-weight: 500;
                    background: #f0ece8;
                    color: #777;
                }

                /* 공통 */
                .cp-center { text-align: center; padding: 80px 20px; color: #888; }
                .cp-notice-title { font-size: 1.05rem; font-weight: 600; color: #495057; margin: 0 0 8px 0; }
                .cp-notice-sub { font-size: 0.875rem; color: #868e96; margin: 0; line-height: 1.6; }
                .cp-spinner {
                    width: 30px; height: 30px;
                    border: 3px solid #e9ecef;
                    border-top-color: #f16102;
                    border-radius: 50%;
                    animation: cpSpin 0.9s linear infinite;
                    margin: 0 auto 14px;
                }
                @keyframes cpSpin { to { transform: rotate(360deg); } }

                /* 반응형 */
                @media (max-width: 1024px) {
                    .cp-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media (max-width: 700px) {
                    .cp-wrap { --side: 20px; }
                    .cp-header { padding: 28px 20px 20px; }
                    .cp-header-inner { flex-direction: column; align-items: flex-start; gap: 14px; }
                    .cp-search-box { width: 100%; }
                    .cp-filter-bar { padding: 0 20px; }
                    .cp-filter-bar-inner { height: auto; padding: 10px 0; flex-wrap: wrap; gap: 8px; }
                    .cp-section { padding: 20px 20px 60px; }
                    .cp-grid { grid-template-columns: 1fr; gap: 12px; }
                }
            `}</style>
        </>
    );
}
